import { Box, Stack, Typography } from '@mui/material';
import { Loader } from 'src/components';
import Iconify from 'src/components/Iconify';
import { formatCompactCount } from 'src/utils/formatNumber.util';

// The card shell, headings, empty state and tooltip box every overview chart shares, so the
// unmatched files overview and the pages that borrow its look stay in step.
export const OVERVIEW_CARD_SX = {
  backgroundColor: '#454545',
  border: '1px solid #5B5B5B',
  borderRadius: '8px',
  p: 1.5,
};

// A card's heading follows the weight the surrounding page gives it: lg where the card is the
// page's own content, sm where the same card is reused as a small overview beside a table.
const CARD_TITLE_SIZES = {
  sm: { title: '0.74rem', subtitle: '0.62rem' },
  md: { title: '0.82rem', subtitle: '0.68rem' },
  lg: { title: '0.95rem', subtitle: '0.72rem' },
};

export const CardTitle = ({ title, subtitle, size = 'md' }) => {
  const step = CARD_TITLE_SIZES[size] ?? CARD_TITLE_SIZES.md;

  return (
    <Stack gap={0.25}>
      <Typography
        sx={{
          color: '#FFFFFF',
          fontWeight: 700,
          fontSize: step.title,
          lineHeight: 1.3,
        }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography sx={{ color: '#9CA3AF', fontSize: step.subtitle, lineHeight: 1.3 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Stack>
  );
};

export const EmptyChartState = ({ height = 120, message = 'No data yet' }) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    sx={{
      height,
      border: '1px dashed #6B7280',
      borderRadius: '8px',
      backgroundColor: 'rgba(0, 0, 0, 0.08)',
      px: 1,
    }}>
    <Typography align="center" sx={{ color: '#CFCFCF', fontSize: '0.72rem' }}>
      {message}
    </Typography>
  </Stack>
);

// Sized to sit inside a card without the spinner dominating it, and without a dashed placeholder
// border, so a loading card reads as the chart arriving rather than as an empty one.
export const ChartLoadingState = ({ height = 120 }) => (
  <Stack alignItems="center" justifyContent="center" sx={{ height }}>
    <Loader size="44px" />
  </Stack>
);

export const LegendPill = ({ color, label, value, selected, onClick }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={0.6}
    onClick={onClick}
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      borderRadius: '6px',
      border: selected ? '1px solid #7DFF92' : '1px solid transparent',
      backgroundColor: selected ? 'rgba(125, 255, 146, 0.08)' : 'transparent',
      px: 0.65,
      py: 0.3,
      minWidth: 0,
    }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
    <Typography sx={{ color: '#F3F4F6', fontSize: '0.68rem' }}>{label}</Typography>
    {value == null ? null : (
      <Typography sx={{ color: '#CFCFCF', fontSize: '0.68rem' }}>
        {typeof value === 'number' ? formatCompactCount(value) : value}
      </Typography>
    )}
  </Stack>
);

// A numbered step in a "Get Started" card.
export const StepRow = ({ index, title, detail }) => (
  <Stack direction="row" gap={1.25} alignItems="flex-start">
    <Box
      sx={{
        flexShrink: 0,
        width: 22,
        height: 22,
        borderRadius: '50%',
        border: '1px solid #87C8E0',
        color: '#87C8E0',
        fontSize: '0.72rem',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mt: '1px',
      }}>
      {index}
    </Box>
    <Stack gap={0.1} sx={{ minWidth: 0 }}>
      <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.78rem', lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Typography sx={{ color: '#9CA3AF', fontSize: '0.68rem', lineHeight: 1.3 }}>
        {detail}
      </Typography>
    </Stack>
  </Stack>
);

// One thing the environment has that is worth acting on. Without onClick it reads as a statement
// rather than a control, which is how the all-clear version of it is used.
export const DetectedCallout = ({ icon, accent, title, subtitle, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      display: 'flex',
      gap: 1,
      alignItems: 'center',
      borderRadius: '8px',
      border: `1px solid ${accent}66`,
      backgroundColor: `${accent}1F`,
      px: 1,
      py: 1,
      transition: 'border-color 0.15s, background-color 0.15s',
      ...(onClick ? { '&:hover': { borderColor: accent, backgroundColor: `${accent}33` } } : {}),
    }}>
    <Iconify icon={icon} width={20} height={20} color={accent} style={{ flexShrink: 0 }} />
    <Stack gap={0.1} sx={{ minWidth: 0 }}>
      <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.74rem', lineHeight: 1.2 }}>
        {title}
      </Typography>
      <Typography sx={{ color: '#CFCFCF', fontSize: '0.66rem', lineHeight: 1.2 }}>
        {subtitle}
      </Typography>
    </Stack>
  </Box>
);

export const DETECTED_HEADING_SX = {
  color: '#9CA3AF',
  fontSize: '0.62rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

// Nothing outstanding is worth saying out loud, so it gets the same shape as a callout in green.
export const AllClearCallout = ({ subtitle = 'Check back later as new activity is reported.' }) => (
  <DetectedCallout
    icon="mdi:party-popper"
    accent="#7DFF92"
    title="Nothing needs your attention right now"
    subtitle={subtitle}
  />
);

export const ChartTooltipBox = ({ children }) => (
  <Box
    sx={{
      backgroundColor: '#FFFFFF',
      color: '#111827',
      border: '1px solid #D1D5DB',
      borderRadius: '4px',
      px: 1,
      py: 0.75,
      fontSize: 12,
      maxWidth: 320,
      // Long names with no spaces would otherwise run straight out of the box.
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
    }}>
    {children}
  </Box>
);
